import React, { useState, useEffect } from 'react';
import { MessageSquare, Bug, Lightbulb, HelpCircle, Star, CheckCircle, Eye, Trash2, User, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config/api';

const StatCard: React.FC<{ icon: React.ReactNode; value: number | string; label: string; color: string }> = ({ icon, value, label, color }) => {
    const colors: Record<string, string> = {
        green: 'from-green-50 to-green-100 border-green-200 text-green-700',
        blue: 'from-blue-50 to-blue-100 border-blue-200 text-blue-700',
        purple: 'from-purple-50 to-purple-100 border-purple-200 text-purple-700',
        orange: 'from-orange-50 to-orange-100 border-orange-200 text-orange-700',
        red: 'from-red-50 to-red-100 border-red-200 text-red-700',
        yellow: 'from-yellow-50 to-yellow-100 border-yellow-200 text-yellow-700',
    };
    return (
        <div className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-2.5 text-center`}>
            <div className="flex justify-center mb-1 opacity-60 [&>svg]:w-4 [&>svg]:h-4">{icon}</div>
            <p className="text-xl font-semibold">{value}</p>
            <p className="text-[10px] opacity-60 uppercase tracking-wide">{label}</p>
        </div>
    );
};

interface FeedbackItem {
  id: number;
  user_id: number;
  username: string | null;
  user_email: string | null;
  feedback_type: string;
  subject: string;
  message: string;
  rating: number | null;
  status: string;
  admin_response: string | null;
  created_at: string | null;
  responded_at: string | null;
}

interface FeedbackStats {
  total: number;
  by_type: {
    bug: number;
    feature: number;
    general: number;
    question: number;
  };
  by_status: {
    pending: number;
    reviewed: number;
    resolved: number;
  };
  average_rating: number;
}

const FeedbackManagement: React.FC = () => {
  const { token } = useAuth();
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [stats, setStats] = useState<FeedbackStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackItem | null>(null);
  const [responseText, setResponseText] = useState('');
  const [filter, setFilter] = useState<string>('all');

  const fetchData = async () => {
    setLoading(true);
    try {
      const typeParam = filter !== 'all' ? `&feedback_type=${filter}` : '';
      const [feedbackRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/admin/feedback?limit=100${typeParam}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/admin/feedback/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (feedbackRes.ok) {
        const data = await feedbackRes.json();
        setFeedback(data.items || []);
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (err) {
      setError('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token, filter]);

  const handleStatusUpdate = async (id: number, newStatus: string, response?: string) => {
    try {
      const params = new URLSearchParams({ status: newStatus });
      if (response) params.append('admin_response', response);

      const res = await fetch(`${API_URL}/admin/feedback/${id}?${params}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        fetchData();
        setSelectedFeedback(null);
        setResponseText('');
      }
    } catch (err) {
      setError('Güncelleme başarısız');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Bu geri bildirimi silmek istediğinize emin misiniz?')) return;

    try {
      const res = await fetch(`${API_URL}/admin/feedback/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        setFeedback(feedback.filter(f => f.id !== id));
        fetchData();
      }
    } catch (err) {
      setError('Silme işlemi başarısız');
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('tr-TR');
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'bug': return <Bug className="w-4 h-4 text-red-600" />;
      case 'feature': return <Lightbulb className="w-4 h-4 text-amber-600" />;
      case 'question': return <HelpCircle className="w-4 h-4 text-blue-600" />;
      default: return <MessageSquare className="w-4 h-4 text-purple-600" />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'bug': return 'Hata';
      case 'feature': return 'Öneri';
      case 'question': return 'Soru';
      default: return 'Genel';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">Bekliyor</span>;
      case 'reviewed':
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">İncelendi</span>;
      case 'resolved':
        return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Çözüldü</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">{status}</span>;
    }
  };

  const renderStars = (rating: number | null) => {
    if (!rating) return '-';
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map(i => (
          <Star
            key={i}
            className={`w-3 h-3 ${i <= rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Geri Bildirimler</h1>
          <p className="text-sm text-gray-500 mt-1">{feedback.length} geri bildirim</p>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 p-4 rounded-2xl mb-6">{error}</div>
        )}

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
            <StatCard icon={<MessageSquare />} value={stats.total} label="Toplam" color="purple" />
            <StatCard icon={<Bug />} value={stats.by_type.bug} label="Hata" color="red" />
            <StatCard icon={<Lightbulb />} value={stats.by_type.feature} label="Öneri" color="orange" />
            <StatCard icon={<HelpCircle />} value={stats.by_type.question} label="Soru" color="blue" />
            <StatCard icon={<MessageSquare />} value={stats.by_status.pending} label="Bekliyor" color="yellow" />
            <StatCard icon={<Eye />} value={stats.by_status.reviewed} label="İncelendi" color="blue" />
            <StatCard icon={<CheckCircle />} value={stats.by_status.resolved} label="Çözüldü" color="green" />
            <StatCard icon={<Star />} value={stats.average_rating} label="Ort. Puan" color="orange" />
          </div>
        )}

        {/* Filter Tabs */}
        <div className="bg-white rounded-2xl p-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {['all', 'bug', 'feature', 'general', 'question'].map(type => (
              <button
                key={type}
                onClick={() => setFilter(type)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  filter === type
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                {type === 'all' ? 'Tümü' : getTypeLabel(type)}
              </button>
            ))}
          </div>
        </div>

        {/* Feedback Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {feedback.map(item => (
            <div
              key={item.id}
              className="bg-white rounded-2xl p-4 hover:ring-2 hover:ring-purple-200 transition-all cursor-pointer"
              onClick={() => setSelectedFeedback(item)}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    {getTypeIcon(item.feedback_type)}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{item.subject}</p>
                    <p className="text-xs text-gray-500">{item.username || '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusBadge(item.status)}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                    className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <p className="text-sm text-gray-600 line-clamp-2 mb-3">{item.message}</p>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {renderStars(item.rating)}
                </div>
                <p className="text-xs text-gray-400">{formatDate(item.created_at)}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Empty */}
        {feedback.length === 0 && (
          <div className="bg-white rounded-2xl p-12 text-center">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Geri bildirim bulunamadı</p>
          </div>
        )}

        {/* Detail Modal */}
        {selectedFeedback && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      {getTypeIcon(selectedFeedback.feedback_type)}
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">{selectedFeedback.subject}</h2>
                      <p className="text-sm text-gray-500">{selectedFeedback.username}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedFeedback(null)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Tür:</span>
                    <span className="text-sm font-medium">{getTypeLabel(selectedFeedback.feedback_type)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Puan:</span>
                    {renderStars(selectedFeedback.rating)}
                  </div>
                  {getStatusBadge(selectedFeedback.status)}
                </div>

                <div>
                  <p className="text-sm text-gray-500 mb-2">Mesaj</p>
                  <p className="bg-gray-50 p-4 rounded-xl text-sm text-gray-700 whitespace-pre-wrap">{selectedFeedback.message}</p>
                </div>

                {selectedFeedback.admin_response && (
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Admin Yanıtı</p>
                    <p className="bg-purple-50 p-4 rounded-xl text-sm text-purple-700">{selectedFeedback.admin_response}</p>
                  </div>
                )}

                <div>
                  <p className="text-sm text-gray-500 mb-2">Yanıt Yaz</p>
                  <textarea
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl p-3 h-24 text-sm outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Kullanıcıya yanıt yazın..."
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleStatusUpdate(selectedFeedback.id, 'reviewed', responseText)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    İncelendi
                  </button>
                  <button
                    onClick={() => handleStatusUpdate(selectedFeedback.id, 'resolved', responseText)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700 transition-colors"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Çözüldü
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackManagement;
